import { faArrowDownToLine } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Domain, ERegistrars } from '@feathr/blackbox';
import { Button, Card, Collapse, Fieldset, Table, Toolbar } from '@feathr/components';

import type { TDomainRecord } from './DomainRecords.hooks';
import { useDomainRecords } from './DomainRecords.hooks';
import domainRecordsColumns from './domainRecordsColumns';

import * as styles from './DomainRecordsTable.css';

interface IProps {
  domain: Domain;
  format: ERegistrars;
}

function DomainRecordsTable({ domain, format }: IProps): JSX.Element {
  const { t } = useTranslation();

  const { isLoading, records } = useDomainRecords(domain, format);

  return (
    <Card className={styles.card}>
      <Collapse title={t('View and download your domain records')}>
        <Fieldset
          className={styles.fieldset}
          helpText={t(
            'Use this tool to view your domain records. If you selected a DNS host above, these records will be formatted to seamlessly work with them.',
          )}
        >
          <Toolbar>
            <Button
              className={styles.downloadButton}
              href={domain.getDownloadUrl(format)}
              suffix={<FontAwesomeIcon icon={faArrowDownToLine} />}
              type={'primary'}
            >
              {t('Download records')}
            </Button>
          </Toolbar>
          <Table<TDomainRecord>
            columns={domainRecordsColumns(t)}
            idKey={'value'}
            isLoading={isLoading}
            items={records}
          />
        </Fieldset>
      </Collapse>
    </Card>
  );
}

export default observer(DomainRecordsTable);
